import moment from "moment-timezone";
import type { Message } from "../types/messaging";

/**
 * Group a conversation's messages into day buckets for the chat view's date
 * dividers. Days are computed in the **viewer's local timezone**: the API sends
 * each `created_dt` as a naive UTC string, so we parse it as UTC and convert to
 * local. Each bucket is labelled Today / Yesterday / "MMM D, YYYY". Input order
 * (oldest→newest, as the thread API returns it) is preserved within and across
 * groups.
 */
export interface MessageDayGroup {
  /** YYYY-MM-DD in the viewer's local timezone — a stable React key. */
  key: string;
  label: string;
  messages: Message[];
}

export function groupMessagesByDay(messages: Message[]): MessageDayGroup[] {
  const today = moment().startOf("day");
  const yesterday = today.clone().subtract(1, "day");

  const groups: MessageDayGroup[] = [];
  const byKey = new Map<string, MessageDayGroup>();

  for (const message of messages) {
    const at = moment.utc(message.created_dt).local();
    const key = at.format("YYYY-MM-DD");
    let group = byKey.get(key);
    if (!group) {
      const dayStart = at.clone().startOf("day");
      let label: string;
      if (dayStart.isSame(today, "day")) {
        label = "Today";
      } else if (dayStart.isSame(yesterday, "day")) {
        label = "Yesterday";
      } else {
        label = at.format("MMM D, YYYY");
      }
      group = { key, label, messages: [] };
      byKey.set(key, group);
      groups.push(group);
    }
    group.messages.push(message);
  }

  return groups;
}
